export interface BiomeDef {
  name: string;
  sky: readonly [string, string];
  ground: string;
  /** Painted background (public/ path). Without one, the zone draws its procedural sky + ground. */
  image?: string;
  /** Where the fighting floor sits in this zone, as a fraction of battlefield height (default 0.8). */
  groundAt?: number;
  /** Regular Gloom toys of this room (docs/DESIGN.md §7) and its Giant. */
  enemies: readonly [string, string, string, string];
  giant: string;
  /** Painted enemy sprites (public/ paths), by enemies slot a-d. Missing slots draw the procedural blob. */
  enemyImages?: readonly [string?, string?, string?, string?];
  /** Painted Giant sprite (public/ path). */
  giantImage?: string;
}

export const STAGES_PER_BIOME = 10;

/**
 * Painted art for room n (handoff/art-inbox/batch2, arena look): its background, four enemies
 * (enemy_zNN_a..d_idle) and Giant (boss_zNN_idle). Every arena background puts its rug's middle at
 * 74% height, so the enemy stands on the back half of the rug and Tuck on the front.
 */
function roomArt(n: number, bg = `zone_z${String(n).padStart(2, '0')}_bg.png`): Pick<BiomeDef, 'image' | 'groundAt' | 'enemyImages' | 'giantImage'> {
  const z = `z${String(n).padStart(2, '0')}`;
  return {
    image: `art/${bg}`,
    groundAt: 0.74,
    enemyImages: ['a', 'b', 'c', 'd'].map((l) => `art/enemy_${z}_${l}_idle.png`) as [string, string, string, string],
    giantImage: `art/boss_${z}_idle.png`,
  };
}


/** The 10 zones of Ferrum (docs/DESIGN.md §7). Each spans STAGES_PER_BIOME stages, then the cycle repeats. */
/** The 10 rooms of the house (docs/DESIGN.md §7). Each spans STAGES_PER_BIOME stages, then the cycle repeats.
 *  Add `image` (+ `groundAt`) per room as the painted backgrounds arrive. */
export const BIOMES: readonly BiomeDef[] = [
  { name: 'Bedtime Boulevard',    sky: ['#5b3a7a', '#f2a36b'], ground: '#8a5a3c',
    ...roomArt(1, 'zone_z01_bg_v3.png'),
    enemies: ['Sock Snake', 'Alarm-Clock Brute', 'Grumpy Night-Light', 'Marble Beetle'], giant: 'Nightmare Jack' },
  { name: 'Playroom Plains',      sky: ['#79c3f2', '#fff1c1'], ground: '#d95f4b',
    ...roomArt(2, 'zone_z02_bg_v3.png'),
    enemies: ['Block Golem', 'Runaway Train', 'Spinning Top', 'Bouncy Ball'], giant: 'King Grizzle' },
  { name: 'Kitchen Canyon',       sky: ['#dfeef5', '#fffaf0'], ground: '#b8c4cc',
    ...roomArt(3),
    enemies: ['Toaster Tank', 'Whisk Whirl', 'Salt-Shaker Knight', 'Cookie Crumbler'], giant: 'Blendzilla' },
  { name: 'Bubble Bath Bay',      sky: ['#8fd7ea', '#e9fbff'], ground: '#5fb3c8',
    ...roomArt(4),
    enemies: ['Rogue Rubber Duck', 'Soap Slime', 'Toothbrush Lancer', 'Sponge Brute'], giant: 'Admiral Quack' },
  { name: 'Backyard Jungle',      sky: ['#8fd16b', '#f2ffd9'], ground: '#4f8f3a',
    ...roomArt(5),
    enemies: ['Snail Tank', 'Garden Gnome Grump', 'Sprinkler Hydra', 'Beetle Knight'], giant: 'Mow-Zilla' },
  { name: 'Garage Gauntlet',      sky: ['#3d3a36', '#f0a04b'], ground: '#6b6660',
    ...roomArt(6),
    enemies: ['RC Racer', 'Wrench Crab', 'Paint-Can Bomber', 'Drill Driller'], giant: 'Turbo Tyrant' },
  { name: 'Classroom Chaos',      sky: ['#2f5a45', '#f5e6b8'], ground: '#b3834d',
    ...roomArt(7),
    enemies: ['Pencil Pikeman', 'Eraser Blob', 'Glue-Stick Goo', 'Ruler Rattler'], giant: 'Stapler Rex' },
  { name: 'Living Room Wastes',   sky: ['#2a2f55', '#6b86c9'], ground: '#7a5a48',
    ...roomArt(8),
    enemies: ['Remote-Control Crab', 'Dust Bunny', 'Cushion Golem', 'Cable Snake'], giant: 'Hoover Horror' },
  { name: 'Arcade After Dark',    sky: ['#1b0f33', '#c2248c'], ground: '#2a1f44',
    ...roomArt(9),
    enemies: ['Pinball Brawler', 'Pixel Ghost', 'Joystick Knight', 'Token Swarm'], giant: 'Claw King' },
  { name: 'The Forgotten Attic',  sky: ['#1a1230', '#6b4a9e'], ground: '#4a3a33',
    ...roomArt(10),
    enemies: ['Moth Swarm', 'Dusty Doll', 'Cobweb Spider', 'Broken Jack'], giant: 'The Forgotten King' },
];

export function biomeForStage(stage: number): BiomeDef {
  return BIOMES[Math.floor((stage - 1) / STAGES_PER_BIOME) % BIOMES.length]!;
}

/** Which of a room's four regular enemies (0-3 = a-d) a monster is; shared by its name and its sprite. */
export function enemySlot(seed: number): number {
  return seed % 4;
}

/** Display name of the current monster. */
export function monsterName(b: BiomeDef, m: { boss: boolean; seed: number }): string {
  return m.boss ? b.giant : b.enemies[enemySlot(m.seed)]!;
}

/** Painted sprite for the current monster, if its art has landed. */
/** The hit pose that pairs with an enemy or Giant idle (Batch 3: `*_hit.png`, same canvas). */
export function hitImage(idle: string): string {
  return idle.replace('_idle.png', '_hit.png');
}

export function monsterImage(b: BiomeDef, m: { boss: boolean; seed: number }): string | undefined {
  return m.boss ? b.giantImage : b.enemyImages?.[enemySlot(m.seed)];
}
